"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Ban, Percent, Receipt } from "lucide-react";

// No fixed price table: the customer pays Uber Direct's live courier quote.
const TIERS = [
  { range: { en: "Who quotes it", es: "Quién lo cotiza" }, price: "Uber Direct" },
  { range: { en: "When they see it", es: "Cuándo lo ven" }, price: { en: "Before paying", es: "Antes de pagar" } },
  { range: { en: "What moves it", es: "Qué lo mueve" }, price: { en: "Distance & demand", es: "Distancia y demanda" } },
];

const MERCHANT_FACTS = [
  {
    icon: Receipt,
    title: { en: "From $2.99 per delivered delivery", es: "Desde $2.99 por envío entregado" },
    body: {
      en: "One platform fee, set by your plan ($2.99 on Pro, $3.99 on Start), charged per delivery and billed weekly. That's the whole bill from Redy.",
      es: "Una tarifa de plataforma según tu plan ($2.99 en Pro, $3.99 en Start), por envío y facturada cada semana. Esa es toda la cuenta de Redy.",
    },
  },
  {
    icon: Percent,
    title: { en: "0% commission", es: "0% de comisión" },
    body: {
      en: "Nothing comes off the order value. A $45 order and a $145 order cost you the same fee.",
      es: "Nada sale del valor del pedido. Un pedido de $45 y uno de $145 te cuestan la misma tarifa.",
    },
  },
  {
    icon: Ban,
    title: { en: "Cancelled? Redy charges nothing", es: "¿Cancelado? Redy no te cobra nada" },
    body: {
      en: "The fee applies only to deliveries that actually arrive. Cancelled, failed or returned: $0.",
      es: "La tarifa aplica solo a envíos que sí llegan. Cancelado, fallido o devuelto: $0.",
    },
  },
];

export default function DeliveryPricing() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[720px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("What it costs", "Lo que cuesta")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Two numbers. ", "Dos números. ")}
            <span className="text-redy-charcoal/30">{t("That's the price list.", "Esa es la lista de precios.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "The customer pays the courier. You pay the platform fee. There is no third number and no percentage of your food.",
              "El cliente paga el courier. Tú pagas la tarifa de plataforma. No hay un tercer número ni porcentaje sobre tu comida."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* The customer's side — distance tiers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="bg-white rounded-[28px] border border-black/5 p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("The customer pays", "El cliente paga")}
            </div>
            <div className="text-[22px] font-black text-redy-charcoal mb-1">
              {t("The courier, at the live quote", "El courier, a la cotización en vivo")}
            </div>
            <p className="text-[13px] font-medium text-redy-secondary leading-relaxed mb-7">
              {t(
                "Quoted live at checkout before they pay. They see the real number, not an estimate.",
                "Se cotiza en vivo en el checkout antes de que paguen. Ven el número real, no un estimado."
              )}
            </p>

            <div className="divide-y divide-black/5 border-t border-black/5">
              {TIERS.map((tier, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.4, delay: 0.25 + 0.08 * i }}
                  className="flex items-baseline justify-between py-4"
                >
                  <span className="text-[14px] font-bold text-redy-charcoal">
                    {t(tier.range.en, tier.range.es)}
                  </span>
                  <span className="text-[20px] font-black tabular-nums text-redy-charcoal">
                    {typeof tier.price === "string" ? tier.price : t(tier.price.en, tier.price.es)}
                  </span>
                </motion.div>
              ))}
            </div>

            <p className="text-[12px] font-medium text-redy-secondary/60 mt-6 leading-relaxed">
              {t(
                "Beyond 10 miles the address falls outside the delivery zone and the customer is offered pickup instead.",
                "Más allá de 10 millas la dirección queda fuera de la zona de reparto y al cliente se le ofrece pickup."
              )}
            </p>
          </motion.div>

          {/* Your side — from $2.99 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.22, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="relative rounded-[28px] p-8 md:p-10 text-white overflow-hidden shadow-lg shadow-[#F4722B]/20"
            style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-white/70 mb-2">
              {t("You pay", "Tú pagas")}
            </div>
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-[13px] font-bold text-white/70">{t("from", "desde")}</span>
              <span className="text-[64px] md:text-[76px] font-black leading-none tabular-nums">$2.99</span>
              <span className="text-[13px] font-bold text-white/80">
                {t("/ delivered delivery", "/ envío entregado")}
              </span>
            </div>
            <p className="text-[14px] font-medium text-white/80 leading-relaxed mt-4 mb-8">
              {t(
                "That's it. No monthly fee for Delivery and no percentage of the ticket.",
                "Eso es todo. Sin cuota mensual por Delivery y sin porcentaje del ticket."
              )}
            </p>

            <div className="space-y-5 border-t border-white/20 pt-7">
              {MERCHANT_FACTS.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.45, delay: 0.4 + 0.1 * i }}
                    className="flex gap-3.5"
                  >
                    <div className="w-9 h-9 rounded-[12px] bg-white/15 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-[14px] font-black">{t(f.title.en, f.title.es)}</div>
                      <div className="text-[13px] font-medium text-white/75 leading-relaxed mt-0.5">
                        {t(f.body.en, f.body.es)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-[13px] font-medium text-redy-secondary/70 mt-12 max-w-[640px] mx-auto leading-relaxed"
        >
          {t(
            "Courier prices are quoted by Uber Direct and can move with distance, weather and demand — the customer always sees the live number before paying, never after.",
            "Los precios del courier los cotiza Uber Direct y pueden moverse con la distancia, el clima y la demanda — el cliente siempre ve el número en vivo antes de pagar, nunca después."
          )}
        </motion.p>
      </div>
    </section>
  );
}
